
import { useUserStore } from '@bv/stores';
import { generateBreadcrumbs } from '@bv/utils/route/breadcrumbs/generate-breadcrumbs';
import { computed } from 'vue';

import campaignUpgradeImage from '@/assets/images/routes/campaign/upgrade-campaign.png';
import { rolesRoute } from '@/components/Settings/Roles/route';
import { useGeneralSettingStore } from '@/stores/settings/general.store';

const settingsBreadcrumbs = generateBreadcrumbs({
  label: 'settings.settings',
  route: { name: 'settings' },
});

const canAccessScheduleModules = computed(() => {
  const store = useUserStore();
  return !!store.callCenterSettings?.is_report_scheduling_alerts_enabled;
});

const showScheduleModulesTab = computed(() => {
  const store = useUserStore();
  return !!store.callCenterSettings?.is_report_scheduling_alerts_enabled || store.isOwner;
});

const settingsRoutes = {
  path: '/settings',
  name: 'settings',
  meta: {
    header: { title: 'settings.settings', icon: 'bvi-setting' },
    sidebar: [
      {
        title: 'settings.nav.access_management',
        items: [
          {
            title: 'settings.nav.users',
            route: '/settings/users',
            icon: 'bvi-user-multiple',
            permissions: 'read-users',
          },
          {
            title: 'settings.nav.extensions',
            route: '/settings/extensions',
            icon: 'bvi-extensions',
            permissions: 'read-extensions',
          },
          {
            title: 'settings.nav.teams',
            route: '/settings/teams',
            icon: 'bvi-user-group',
            permissions: 'read-teams',
          },
          {
            title: 'settings.nav.roles_and_permissions',
            route: '/settings/roles',
            icon: 'bvi-key',
            permissions: 'read-roles-&-permissions',
          },
        ],
      },
      {
        title: 'settings.nav.call_handling',
        items: [
          {
            title: 'settings.nav.queues',
            route: '/settings/queues',
            icon: 'bvi-layers',
            permissions: 'read-queues',
          },
          {
            title: 'settings.nav.inbound_routes',
            route: '/settings/inbound-routes',
            icon: 'bvi-call-inbound',
            permissions: 'read-inbound-routes',
          },
          {
            title: 'settings.nav.ivr',
            route: '/settings/ivr',
            icon: 'bvi-tape-recorder',
            permissions: 'read-ivrs',
          },
          {
            title: 'settings.nav.time_group',
            route: '/settings/time_groups',
            icon: 'bvi-calender-time',
            permissions: 'read-time-groups',
          },
          {
            title: 'settings.nav.time_conditions',
            route: '/settings/time_conditions',
            icon: 'bvi-clock',
            permissions: 'read-time-conditions',
          },
        ],
      },
      {
        title: 'settings.nav.audio_library',
        items: [
          {
            title: 'settings.nav.announcement',
            route: '/settings/announcement',
            icon: 'bvi-signal',
            permissions: 'read-announcements',
          },
          {
            title: 'settings.nav.sound_on_hold',
            route: '/settings/sound-on-hold',
            icon: 'bvi-pause',
            permissions: 'read-sound-on-hold',
          },
          {
            title: 'settings.nav.recordings',
            route: '/settings/recordings',
            icon: 'bvi-voice-circle',
            permissions: 'read-recordings',
          },
        ],
      },
      {
        title: 'settings.nav.monitoring_reports',
        items: [
          {
            title: 'settings.nav.usage',
            route: '/settings/usage',
            icon: 'bvi-chart-pie',
            permissions: 'read-usage',
          },
          {
            title: 'settings.nav.call_tags',
            route: '/settings/call-tags',
            icon: 'bvi-tag',
            permissions: 'read-call-tags',
          },
          {
            title: 'settings.nav.schedule_alerts',
            route: '/settings/schedule-alerts',
            icon: 'bvi-notification-status',
            permissions: 'read-alerts',
            visible: showScheduleModulesTab,
          },
          {
            title: 'reports.nav.schedule_reports',
            route: '/settings/schedule-reports',
            icon: 'bvi-envelope-upload',
            permissions: 'read-scheduled-reports',
            visible: showScheduleModulesTab,
          },
        ],
      },
    ],
  },
  children: [
    {
      path: 'users',
      name: 'usersView',
      meta: {
        permissions: 'read-users',
        breadcrumbs: settingsBreadcrumbs({
          label: 'settings.nav.users',
          route: { name: 'usersList' },
        }),
      },
      children: [
        {
          path: '',
          name: 'usersList',
          component: () => import('@/components/Settings/Users/UsersList.vue'),
          meta: { permissions: 'read-users' },
        },
        {
          path: '',
          component: () => import('@/components/Settings/Users/UsersForm.vue'),
          children: [
            {
              path: 'add',
              name: 'add_user',
              meta: {
                permissions: 'write-users',
                canAccess: computed(() => {
                  const store = useUserStore();
                  const service = store.callCenterService;
                  if (!service) {
return true;
}
                  if (service.billing_mode === 'bevatel') {
return true;
}
                  const max = service.maximum_users;
                  const current = service.created_users;
                  if (max === null || max === undefined || current === null || current === undefined) {
return true;
}
                  return current < max;
                }),
                redirectUnauthorized: { name: 'usersList' },
              },
            },
            {
              path: 'edit/:id',
              name: 'edit_users',
              meta: { permissions: 'update-users' },
            },
          ],
        },
      ],
    },
    {
      path: 'extensions',
      name: 'extensionsView',
      meta: {
        permissions: 'read-extensions',
        breadcrumbs: settingsBreadcrumbs({
          label: 'settings.nav.extensions',
          route: { name: 'extensionsList' },
        }),
      },
      children: [
        {
          path: '',
          name: 'extensionsList',
          component: () => import('@/components/Settings/Extensions/ExtensionsList/ExtensionsList.vue'),
        },
      ],
    },
    {
      path: 'queues',
      name: 'queuesView',
      meta: {
        permissions: 'read-queues',
        breadcrumbs: settingsBreadcrumbs({
          label: 'settings.nav.queues',
          route: { name: 'queues' },
        }),
      },
      children: [
        {
          path: '',
          name: 'queues',
          component: () => import('@/components/Settings/Queues/QueuesList/QueuesList.vue'),
          meta: { permissions: 'read-queues' },
        },
        {
          path: 'add',
          name: 'add_queue',
          component: () => import('@/components/Settings/Queues/AddQueues.vue'),
          meta: { permissions: 'write-queues' },
        },
        {
          path: 'edit/:id',
          name: 'edit_queue',
          component: () => import('@/components/Settings/Queues/QueuesList/EditQueue.vue'),
          meta: { permissions: 'update-queues' },
        },
      ],
    },
    {
      path: 'teams',
      meta: {
        breadcrumbs: settingsBreadcrumbs({
          label: 'settings.nav.teams',
          route: { name: 'teams-list' },
        }),
      },
      children: [
        {
          path: '',
          name: 'teams-list',
          component: () => import('@/components/Settings/Teams/List.vue'),
          meta: { permissions: 'read-teams' },
        },
        {
          path: '',
          component: () => import('@/components/Settings/Teams/Form.vue'),
          children: [
            {
              path: 'add',
              name: 'add-team',
              meta: { permissions: 'write-teams' },
            },
            {
              path: 'edit/:id',
              name: 'edit-team',
              meta: { permissions: 'update-teams' },
            },
          ],
        },
      ],
    },
    {
      path: 'recordings',
      name: 'recordingsView',
      meta: {
        permissions: 'read-recordings',
        breadcrumbs: settingsBreadcrumbs({
          label: 'settings.nav.recordings',
          route: { name: 'recordings' },
        }),
      },
      children: [
        {
          path: '',
          name: 'recordings',
          component: () => import('@/components/Settings/Recordings/RecordingsList.vue'),
          meta: { permissions: 'read-recordings' },
        },
        {
          path: 'add',
          name: 'add_recordings',
          component: () => import('@/components/Settings/Recordings/RecordingsForm.vue'),
          meta: { permissions: 'write-recordings' },
        },
        {
          path: 'edit/:id',
          name: 'edit_recordings',
          component: () => import('@/components/Settings/Recordings/RecordingsForm.vue'),
          meta: { permissions: 'update-recordings' },
        },
      ],
    },
    {
      path: 'announcement',
      name: 'announcementView',
      meta: {
        permissions: 'read-announcements',
        breadcrumbs: settingsBreadcrumbs({
          label: 'settings.nav.announcement',
          route: { name: 'announcement' },
        }),
      },
      children: [
        {
          path: '',
          name: 'announcement',
          component: () => import('@/components/Settings/Announcement/AnnouncementList/AnnouncementList.vue'),
          meta: { permissions: 'read-announcements' },
        },
        {
          path: 'add',
          name: 'add_announcement',
          component: () => import('@/components/Settings/Announcement/AnnouncementForm.vue'),
          meta: { permissions: 'write-announcements' },
        },
        {
          path: 'edit/:id',
          name: 'edit_announcement',
          component: () => import('@/components/Settings/Announcement/AnnouncementForm.vue'),
          meta: { permissions: 'update-announcements' },
        },
      ],
    },
    {
      path: 'inbound-routes',
      meta: {
        breadcrumbs: settingsBreadcrumbs({
          label: 'settings.nav.inbound_routes',
          route: { name: 'inbound-routes-list' },
        }),
      },
      children: [
        {
          path: '',
          name: 'inbound-routes-list',
          component: () => import('@/components/Settings/InboundRoutes/List.vue'),
          meta: { permissions: 'read-inbound-routes' },
        },
        {
          path: 'edit/:id',
          name: 'inbound-routes-edit',
          component: () => import('@/components/Settings/InboundRoutes/Form.vue'),
          meta: { permissions: 'update-inbound-routes' },
        },
      ],
    },
    {
      path: 'ivr',
      meta: {
        permissions: 'read-ivrs',
        breadcrumbs: settingsBreadcrumbs({
          label: 'settings.nav.ivr',
          route: { name: 'ivr-list' },
        }),
      },
      children: [
        {
          path: '',
          name: 'ivr-list',
          component: () => import('@/components/Settings/IVR/List.vue'),
          meta: { permissions: 'read-ivrs' },
        },
        {
          path: 'add',
          name: 'ivr-add',
          component: () => import('@/components/Settings/IVR/Form.vue'),
          meta: { permissions: 'write-ivrs' },
          beforeEnter: () => {
            useGeneralSettingStore().getDestinations();
          },
        },
        {
          path: 'edit/:id',
          name: 'ivr-edit',
          component: () => import('@/components/Settings/IVR/Form.vue'),
          meta: { permissions: 'update-ivrs' },
          beforeEnter: () => {
            useGeneralSettingStore().getDestinations();
          },
        },
      ],
    },
    {
      path: 'sound-on-hold',
      meta: {
        breadcrumbs: settingsBreadcrumbs({
          label: 'settings.nav.sound_on_hold',
          route: { name: 'sound-on-hold-list' },
        }),
      },
      children: [
        {
          path: '',
          name: 'sound-on-hold-list',
          component: () => import('@/components/Settings/SoundOnHold/List.vue'),
          meta: { permissions: 'read-sound-on-hold' },
        },
        {
          path: 'add',
          name: 'sound-on-hold-add',
          component: () => import('@/components/Settings/SoundOnHold/Form.vue'),
          meta: { permissions: 'write-sound-on-hold' },
        },
        {
          path: 'edit/:id',
          name: 'sound-on-hold-edit',
          component: () => import('@/components/Settings/SoundOnHold/Form.vue'),
          meta: { permissions: 'update-sound-on-hold' },
        },
      ],
    },
    {
      path: 'time_conditions',
      name: 'timeConditionsView',
      meta: {
        permissions: 'read-time-conditions',
        breadcrumbs: settingsBreadcrumbs({
          label: 'settings.nav.time_conditions',
          route: { name: 'timeConditionsList' },
        }),
      },
      children: [
        {
          path: '',
          name: 'timeConditionsList',
          component: () => import('@/components/Settings/TimeConditions/TimeConditionsList/TimeConditionsList.vue'),
          meta: { permissions: 'read-time-conditions' },
        },
        {
          path: 'add',
          name: 'add_timeConditions',
          component: () => import('@/components/Settings/TimeConditions/AddTimeCondition.vue'),
          meta: { permissions: 'write-time-conditions' },
        },
        {
          path: 'edit/:id',
          name: 'edit_timeConditions',
          component: () => import('@/components/Settings/TimeConditions/TimeConditionsList/EditTimeConditions.vue'),
          meta: { permissions: 'update-time-conditions' },
        },
      ],
    },
    rolesRoute,
    {
      path: 'time_groups',
      name: 'timeGroupsView',
      meta: {
        permissions: 'read-time-groups',
        breadcrumbs: settingsBreadcrumbs({
          label: 'settings.nav.time_group',
          route: { name: 'timeGroupsList' },
        }),
      },
      children: [
        {
          path: '',
          name: 'timeGroupsList',
          component: () => import('@/components/Settings/TimeGroups/TimeGroups/TimeGroups.vue'),
          meta: { permissions: 'read-time-groups' },
        },
        {
          path: 'edit/:id',
          name: 'edit_timeGroups',
          component: () => import('@/components/Settings/TimeGroups/TimeGroups/EditTimeGroup.vue'),
          meta: { permissions: 'update-time-groups' },
        },
        {
          path: 'add',
          name: 'add_timeGroups',
          component: () => import('@/components/Settings/TimeGroups/AddTimeGroup.vue'),
          meta: { permissions: 'write-time-groups' },
        },
      ],
    },
    {
      path: 'schedule-reports',
      name: 'schedule-reports',
      meta: {
        canAccess: canAccessScheduleModules,
        redirectUnauthorized: { path: '/dashboard' },
        upgradeState: {
          image: campaignUpgradeImage,
          title: 'settings.schedule_reports.upgrade.title',
          description: 'settings.schedule_reports.upgrade.subtitle',
        },
        breadcrumbs: settingsBreadcrumbs({
          label: 'reports.nav.schedule_reports',
          route: { name: 'schedule-reports-list' },
        }),
      },
      children: [
        {
          path: '',
          name: 'schedule-reports-list',
          component: () => import('@/components/Settings/ScheduleReports/List.vue'),
          meta: { permissions: 'read-scheduled-reports' },
        },
        {
          path: 'add',
          name: 'schedule-reports-create',
          component: () => import('@/components/Settings/ScheduleReports/Form.vue'),
          meta: { permissions: 'create-scheduled-reports' },
        },
        {
          path: 'edit/:id',
          name: 'schedule-reports-edit',
          component: () => import('@/components/Settings/ScheduleReports/Form.vue'),
          meta: { permissions: 'update-scheduled-reports' },
        },
      ],
    },
    {
      path: 'usage',
      name: 'usageView',
      component: () => import('@/components/Settings/Usage/UsageView.vue'),
      meta: {
        permissions: 'read-usage',
        breadcrumbs: settingsBreadcrumbs({
          label: 'settings.nav.usage',
          route: { name: 'usageView' },
        }),
      },
    },
    {
      path: 'call-tags',
      meta: {
        breadcrumbs: settingsBreadcrumbs({
          label: 'settings.nav.call_tags',
          route: { name: 'call-tags-list' },
        }),
      },
      children: [
        {
          path: '',
          name: 'call-tags-list',
          component: () => import('@/components/Settings/CallTags/List.vue'),
          meta: { permissions: 'read-call-tags' },
        },
        {
          path: '',
          component: () => import('@/components/Settings/CallTags/Form.vue'),
          children: [
            {
              path: 'add',
              name: 'add-call-tag',
              meta: { permissions: 'write-call-tags' },
            },
            {
              path: 'edit/:id',
              name: 'edit-call-tag',
              meta: { permissions: 'update-call-tags' },
            },
          ],
        },
      ],
    },
    {
      path: 'schedule-alerts',
      name: 'schedule-alerts',
      meta: {
        canAccess: canAccessScheduleModules,
        redirectUnauthorized: { path: '/dashboard' },
        upgradeState: {
          image: campaignUpgradeImage,
          title: 'settings.schedule_alerts.upgrade.title',
          description: 'settings.schedule_alerts.upgrade.subtitle',
        },
        breadcrumbs: settingsBreadcrumbs({
          label: 'settings.nav.schedule_alerts',
          route: { name: 'schedule-alerts-list' },
        }),
      },
      children: [
        {
          path: '',
          name: 'schedule-alerts-list',
          component: () => import('@/components/Settings/ScheduleAlerts/List.vue'),
          meta: { permissions: 'read-alerts' },
        },
        {
          path: 'add',
          name: 'schedule-alerts-create',
          component: () => import('@/components/Settings/ScheduleAlerts/Form.vue'),
          meta: { permissions: 'create-alerts' },
        },
        {
          path: 'edit/:id',
          name: 'schedule-alerts-edit',
          component: () => import('@/components/Settings/ScheduleAlerts/Form.vue'),
          meta: { permissions: 'update-alerts' },
        },
      ],
    },
  ],
};
export default settingsRoutes;
